import React from 'react';
import { subDays } from 'date-fns';
import { stringify } from 'querystring';
import { Map, List } from 'immutable';
import { liveSessionManager } from '@saucelabs/sl-util-axios';
import { constants as jobConstants } from '@saucelabs/sl-module-jobs';
import { utils as rootCauseUtils } from '@saucelabs/sl-module-root-cause';
import Sentry from '@saucelabs/sl-util-sentry';

import {
  AUTOMATED_JOBS_NATIVE_FRAMEWORKS,
  PERFORMANCE_TRACE_COMMAND_DELAY_IN_MS,
  SAUCE_JANKINESS_CUSTOM_COMMAND,
  PERFORMANCE_DISABLE_COMMAND,
  PERFORMANCE_ENABLE_COMMAND,
  FALLBACK_CRASH_TITLE,
} from './constants';
import { constants as commandsConstants } from '../../components/WebDriverCommands';
import { utils as WebDriverCommandsUtils } from '../../webdriver-commands';
import { records, functions } from './../batch-framework-commands';

const { ROOT_CAUSE_TYPE } = commandsConstants;

export function sanitizeCommandId({ hash }) {
  if (hash) {
    const hashValue = (hash[0] === '#') ? hash.substr(1) : hash;
    const commandId = parseInt(hashValue, 10);

    if (Number.isFinite(commandId)) {
      return commandId;
    }
  }
  return null;
}

const commandMatch = (phrase, command) => {
  const searchableFields = [
    command.method,
    (typeof command.getMethod === 'function') && command.getMethod(),
    command.path,
    JSON.stringify(command.request),
    JSON.stringify(command.result),
  ];
  return phrase.test(searchableFields.join(' '));
};

/**
   * Determines whether the regex matches for the given test case of native framework job
   * @param {string} phrase
   * @param {Object} [command]
   * @returns {boolean}
   */
const testCaseMatch = (phrase, command) => {
  const searchableFields = [
    command.name,
  ];

  command.tests.forEach((test) => searchableFields.push(
    test.name,
    test.failure_reason,
  ));

  return phrase.test(searchableFields.join(' '));
};

/**
 * Calculates date from current timestamp. Accepts timestamps in seconds or milliseconds.
 * @param {number | undefined} timestamp
 * @returns {Date}
 */
const getDateFromTimestamp = (timestamp) => {
  if (timestamp) {
    const now = Date.now();
    const isTimestampInSeconds = Math.abs(now - timestamp * 1000) < Math.abs(now - timestamp);
    return new Date(isTimestampInSeconds ? timestamp * 1000 : timestamp);
  }

  return new Date();
};

// If performance's trace timestamp is greater than command's start_time
// and if difference between them is less than PERFORMANCE_TRACE_COMMAND_DELAY_IN_MS time.
export const findPerformanceDataByTimestamp = ({ command, performanceData, addedLoaderIds }) => (
  performanceData.find(({ timestamp, loaderId }) => {
    const timestampDiff = timestamp - (command.get('start_time') * 1000);

    return !addedLoaderIds.includes(loaderId) &&
      timestampDiff >= 0 && timestampDiff <= PERFORMANCE_TRACE_COMMAND_DELAY_IN_MS;
  })
);

export const findRegressionData = (regressions, filterBy) => regressions.find(
  ({ url, order_index: orderIndex }) => url === filterBy.url && orderIndex === filterBy.orderIndex,
);

export const getPerformanceState = (initial = true) => {
  let enabled = initial;

  return (path, request) => {
    const disableCommand = path.includes('execute/sync') && request.script === PERFORMANCE_DISABLE_COMMAND;
    const enableCommand = path.includes('execute/sync') && request.script === PERFORMANCE_ENABLE_COMMAND;

    if (disableCommand) {
      enabled = false;
    } else if (enableCommand) {
      enabled = true;
    }

    return enabled;
  };
};

// Add performance trace data of URL in related command log (either in LOAD URL or Click command)
export const includePerformanceInCommands = ({
  commands, performanceData, performanceRegressions,
}) => {
  // Trace added loaderIds to remove redundancy
  const addedLoaderIds = [];

  const isPerformanceEnabled = getPerformanceState();

  const updateCommand = (command, type) => {
    let filteredPerformanceData;
    const method = command.get('method');
    const path = command.get('path');
    const request = command.get('request');

    if (!isPerformanceEnabled(path, request)) {
      return command;
    }

    const isPostCommand = (type === 'url' && path === 'url' && method === 'POST');
    const isClickCommand = (type === 'click' && path.includes('/click'));
    const hasJankiness = (path.includes('execute/sync') && request.script === SAUCE_JANKINESS_CUSTOM_COMMAND);

    if (isPostCommand || isClickCommand || hasJankiness) {
      filteredPerformanceData = findPerformanceDataByTimestamp({
        command,
        performanceData,
        addedLoaderIds,
      });
    }

    if (filteredPerformanceData) {
      addedLoaderIds.push(filteredPerformanceData.loaderId);
      return command.withMutations((newCommand) => {
        newCommand
          .set('performanceData', filteredPerformanceData)
          .set('regressionData', findRegressionData(performanceRegressions, filteredPerformanceData));
      });
    }
    return command;
  };

  // First check for URL commands
  const newCommands = commands.map((command) => updateCommand(command, 'url'));

  // If all performance data is added in command logs then return it
  if (addedLoaderIds.length === performanceData.size) {
    return newCommands;
  }

  // Next check for Click commands in reverse list of logs
  // to find the exact click command according to timestamp
  return newCommands.reverse().map((command) => updateCommand(command, 'click')).reverse();
};

export const getRootCauseType = (rootCauseLines, currentIndex) => {
  if (!rootCauseLines.has(currentIndex)) {
    return null;
  }

  if (rootCauseLines.size === 1) {
    return ROOT_CAUSE_TYPE.SINGLE;
  }

  if (rootCauseLines.first() === currentIndex) {
    return ROOT_CAUSE_TYPE.START;
  }

  if (rootCauseLines.last() === currentIndex) {
    return ROOT_CAUSE_TYPE.END;
  }

  return ROOT_CAUSE_TYPE.MIDDLE;
};

export const includeRootCauses = ({
  commands, rootCauseLines,
}) => commands.map((command, index) => command.set('rootCauseType', getRootCauseType(rootCauseLines, index)));

export const filterPhrase = (automationBackend, regexPhrase, command) => (
  AUTOMATED_JOBS_NATIVE_FRAMEWORKS.includes(automationBackend) ?
    testCaseMatch(regexPhrase, command) :
    commandMatch(regexPhrase, command)
);

export function escapeRegExp(str) {
  // eslint-disable-next-line no-useless-escape
  return str?.replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&');
}

/**
 * Filter a list where the filterPredicate runs against a deeply nested property.
 * @param {Immutable.List<unknown>} collection - The collection to deeply filter
 * @param {Array<string>} searchPath - The path to the nested collection to filter
 * @param {Function} filterPredicate - A filtering predicate
 */
export function filterInEach(collection, searchPath, filterPredicate = () => (true)) {
  if (!List.isList(collection)) {
    return collection;
  }
  const filtered = collection.map((item) => {
    if (!item.hasIn?.(searchPath)) {
      return item;
    }
    const deepItem = item.getIn(searchPath);

    if (!(List.isList(deepItem) || Map.isMap(deepItem))) {
      return item;
    }

    const nestedCollection = deepItem.filter(filterPredicate);
    return item.setIn(searchPath, nestedCollection);
  });

  return filtered.filter((item) => {
    if (!item.hasIn?.(searchPath)) {
      return item;
    }
    return !item.getIn(searchPath).isEmpty?.();
  });
}

export const filterTestResult = ({ testResult, filterState }) => {
  const {
    phrase,
    showFailedTests,
  } = filterState;

  if (testResult?.has('testsuites')) {
    let newTestSuites = testResult.get('testsuites');
    if (showFailedTests) {
      newTestSuites = filterInEach(
        newTestSuites,
        ['testcases'],
        // TestCase record has a status property, but depending on the type of
        // test result that was parsed , the status may not be set.
        // e.g. junit results do not have an explicit status so we rely on
        // the presence of the failure property.
        (testcase) => testcase.status === 'failed' || testcase.failure != null,
      );
    }
    if (phrase) {
      const regexPhrase = new RegExp(escapeRegExp(phrase), 'i');
      newTestSuites = filterInEach(
        newTestSuites,
        ['testcases'],
        (testcase) => regexPhrase.test(testcase.get('name')),
      );
    }

    if (newTestSuites.size === 0) {
      return null;
    }

    return testResult.set('testsuites', newTestSuites);
  }

  return testResult;
};

export const filterEspressoTests = ({ testClasses, filterState }) => {
  const {
    phrase,
    showFailedTests,
  } = filterState;

  let newTestSuites = testClasses;
  if (showFailedTests) {
    newTestSuites = filterInEach(
      newTestSuites,
      ['tests'],
      (testcase) => testcase.isFailed(),
    );
  }
  if (phrase) {
    const regexPhrase = new RegExp(escapeRegExp(phrase), 'i');
    newTestSuites = filterInEach(
      newTestSuites,
      ['tests'],
      (testcase) => regexPhrase.test(testcase.get('name')));
  }

  return newTestSuites;
};

export const filterCommands = ({
  automationBackend, commands, filterState, performanceData, performanceRegressions, rootCauseLines,
}) => {
  const {
    phrase,
    hasScreenshot,
    hasRootCause,
    hasPerformance,
    showFailedTests,
  } = filterState;
  let newCommands = performanceData && performanceData.size ?
    includePerformanceInCommands({ commands, performanceData, performanceRegressions }) : commands;
  if (rootCauseLines?.size) {
    newCommands = includeRootCauses({ commands: newCommands, rootCauseLines });
  }

  if (!(phrase || hasScreenshot || hasRootCause || hasPerformance || showFailedTests)) {
    return newCommands;
  }

  const regexPhrase = new RegExp(escapeRegExp(phrase), 'i');

  return newCommands
    .filter((command) => (!phrase || filterPhrase(automationBackend, regexPhrase, command)) &&
      (!hasScreenshot || command.hasScreenshot()) &&
      (!hasRootCause || command.get('rootCauseType')) &&
      (!hasPerformance || command.get('performanceData')) &&
      (!showFailedTests || !functions.allTestsInClassPassed({ espressoClass: command })));
};

export function newActiveCommand(commands, offset, currentActive) {
  let newId = currentActive;
  let newCommand;

  // We need to skip certain types of commands so this is a loop:
  do {
    newId += offset;
    if (newId < 0) {
      newId = commands.size - 1;
    } else {
      newId %= commands.size;
    }
    newCommand = commands.get(newId);
  }
  while (newCommand.message);

  return newCommand.get('id');
}

export function preselectedCommandId(commands, hash) {
  const hashCommandId = sanitizeCommandId({ hash });
  if (Number.isFinite(hashCommandId)) {
    return hashCommandId;
  }

  const lastFailedCommandId = commands.findIndex((command) => command.isFailed());

  if (lastFailedCommandId >= 0) {
    return lastFailedCommandId;
  }

  return null;
}

export function screenshotCommandId(commands) {
  let activeCommandIndex = commands.findIndex((command) => command.get('active'));
  if (activeCommandIndex < 0) {
    return null;
  }

  while (
    !commands.get(activeCommandIndex).hasScreenshot() &&
    activeCommandIndex >= 1
  ) {
    activeCommandIndex -= 1;
  }

  if (commands.get(activeCommandIndex).hasScreenshot()) {
    return commands.get(activeCommandIndex);
  }

  return null;
}

export function offsetCommandId(commands, offset, currentActive) {
  let newIndex = currentActive + offset;
  if (newIndex < 0) {
    newIndex = commands.size - 1;
  } else if (newIndex >= commands.size) {
    newIndex = 0;
  }

  if (!commands.get(newIndex)) {
    return null;
  }

  return commands.get(newIndex).get('id');
}

export const createBreadcrumbForLastLocation = (lastLocation, job) => {
  const defaultLocationAutomated = {
    name: 'Test Results',
    url: '/dashboard/tests',
  };
  const defaultLocationManual = {
    name: 'Test Results',
    url: '/dashboard/live',
  };

  if (!lastLocation || !lastLocation.pathname) {
    return job?.isManual() ? defaultLocationManual : defaultLocationAutomated;
  }

  const { pathname, search } = lastLocation;
  const url = `${pathname}${search}`;

  if (pathname.startsWith('/performance')) {
    return { name: 'Performance', url };
  }

  if (pathname.startsWith('/archives')) {
    return { name: 'Archives', url };
  }

  if (pathname.startsWith('/builds')) {
    return { name: 'Builds', url };
  }

  if (pathname.startsWith('/dashboard')) {
    return { name: 'Test Results', url };
  }

  if (pathname.startsWith('/live')) {
    return defaultLocationManual;
  }

  return defaultLocationAutomated;
};

export const hasUserAccessToTheJob = ({ user, job }) => {
  if (
    !user.isAdmin() &&
    !user.isOrgAdmin() &&
    job.getVisibility() === jobConstants.VISIBILITY_PRIVATE &&
    job.get('owner') !== user.get('username')
  ) {
    return false;
  }
  return true;
};

export const getRootCauseQueryParams = ({ jobHash, job, pattern, user }) => {
  const startDate = getDateFromTimestamp(job.get('start_time'));
  const endDate = getDateFromTimestamp(job.get('end_time'));
  const queryParams = {
    testHash: JSON.stringify({
      id: jobHash,
      name: job.get('name'),
    }),
    dateFilter: JSON.stringify({
      type: 'absolute',
      value: {
        startDate: subDays(startDate, 1).toISOString(),
        endDate: endDate.toISOString(),
      },
    }),
    pattern,
    owners: rootCauseUtils.getOwnerScope(user),
  };

  return `?${stringify(queryParams)}`;
};

export const updateVideoTimeline = ({ player, currentTime }) => {
  if (!player || typeof player.currentTime !== 'function') {
    return;
  }
  let playerCurrentTime;
  try {
    playerCurrentTime = player.currentTime();
  } catch {
    return;
  }
  const newTime = Math.max(currentTime, 0);
  player.pause();
  player.currentTime(newTime);

  // we want to hide posterImage when somebody clicks on commands list item
  // when didn't play the video before
  if (playerCurrentTime === 0) {
    player.hasStarted(true);
  }
};

/**
 * Returns the absolute URL to the RFB socket based on
 * the current environment and the provided session_id
 * @param {string} sessionId - session_id of a running automated VDC test
 * @returns {(string|undefined)}
 */
export const createRfbUrl = (sessionId = '') => {
  try {
    const lsmUrl = new URL(liveSessionManager.defaults.baseURL);
    const protocol = (lsmUrl.protocol === 'https:') ? 'wss:' : 'ws:';
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    return `${protocol}//${lsmUrl.host}/v1/lsm/session/${sessionId}/rfb${params.auth ? `?auth=${params.auth}` : ''}`;
  } catch (error) {
    Sentry.captureException(error);
    return undefined;
  }
};

/**
* Returns a mapping function that maps a testcase parsed from the sauce-test-report.json
* format to a batch-framework-commands/records/TestCase
*
* @param {Generator} gen must generate unique IDs.
* @returns {function}
*/
function testCaseMapper(gen) {
  return function mapper(test) {
    let testcase = new records.TestCase({
      name: test.name,
      test_number: gen.next().value,
      time: parseInt(test.duration, 10),
      status: test.status,
      attachments: (test.attachments ?
        // eslint-disable-next-line id-length
        List(test.attachments.map((a) => (
          new records.Attachment({
            ...a,
          })
        ))) :
        List()),
    });
    if (test.status !== 'passed' && test.output && test.output.length > 0) {
      const errMsg = Array.isArray(test.output) ? test.output.join('\n') : test.output;
      const failure = new records.Failure({
        content: errMsg,
        message: errMsg,
      });
      testcase = testcase.set('failure', failure);
    }

    if (test.videoTimestamp) {
      testcase = testcase.set('video_timestamp', test.videoTimestamp);
    }
    return testcase;
  };
}

/**
 * Converts the suites from sauce-test-report into a flat array of type records.Testsuite[]
 *
 * Nested suites will have their names updated to reflect the former hierarchy.
 */
const flattenSuites = (suites, name = '', gen) => {
  const collection = [];

  for (let i = 0; i < suites.length; i += 1) {
    const suite = suites[i];
    if (suite.suites?.length === 0) {
      const testcases = suite.tests.map(testCaseMapper(gen));

      const testsuiteRecord = new records.Testsuite({
        name: `${name} - ${suite.name}`,
        testcases: new List(testcases),
      });
      collection.push(testsuiteRecord);
    } else {
      const nestedName = name ? `${name} - ${suite.name}` : suite.name;

      const flatSuites = flattenSuites(suite.suites, nestedName, gen);
      collection.push(...flatSuites);

      if (suite.tests?.length > 0) {
        const testcases = suite.tests.map(testCaseMapper(gen));

        const testsuiteRecord = new records.Testsuite({
          name: `${name} - ${suite.name}`,
          testcases: new List(testcases),
        });
        collection.push(testsuiteRecord);
      }
    }
  }
  return collection.map((suite, idx) => suite.set('id', idx));
};

/**
 * Parses a sauce-test-report.json object and converts it to a records.TestResult
 */
export const parseSauceTestReport = (testReport) => {
  function* idGenerator() {
    let i = 1;
    while (true) {
      yield (i);
      i += 1;
    }
  }

  const flattenedSuites = flattenSuites(testReport.suites, '', idGenerator());

  const parsedReport = new records.TestResult({
    testsuites: new List(flattenedSuites),
  });
  return parsedReport;
};

export const extractCrashedThreadData = (stackTrace) => {
  const crashedThreadRegex = /Crashed Thread:\s+(\d+)/;
  const crashedThread = stackTrace.match(crashedThreadRegex)[1];

  const crashedThreadString = `Thread ${crashedThread} Crashed:`;
  const crashedThreadIndex = stackTrace.indexOf(crashedThreadString);
  const nextLineIndex = stackTrace.indexOf('\n', crashedThreadIndex + crashedThreadString.length);
  return { descriptionData: stackTrace.slice(nextLineIndex).split('\n').slice(0, 6), crashedThreadString };
};

/**
 * Parses a stackTrace string and returns title and description
 */
export const extractiOStitleandDesc = (stackTrace) => {
  const excRegex = /Exception Type:\s+(\S+)/;

  let title = '';

  let description = '';

  try {
    title = stackTrace.match(excRegex)[0];

    const { descriptionData, crashedThreadString } = extractCrashedThreadData(
      stackTrace);

    description = (
      <div>
        {crashedThreadString}
        {descriptionData.map((item) => <p>{item}</p>)}
      </div>
    );
  } catch (error) {
    title = FALLBACK_CRASH_TITLE;
  }

  return { title, description };
};

const AndroidTitleRegex = new RegExp(/^.*(\s*at)/);

/**
 * Parses a stackTrace string and returns title for Android
 */
export const extractAndroidTitle = (stackTrace) => {
  let title = stackTrace.match(AndroidTitleRegex)?.[0];

  if (!title) {
    let delimiter = '\n';

    /* sometimes space is represented as empty string or \n we split the stack trace as we find
    any of those delimiters */

    if (stackTrace.indexOf(delimiter) > stackTrace.indexOf(' ')) {
      delimiter = ' ';
    }

    title = stackTrace.split(delimiter)?.[0] || FALLBACK_CRASH_TITLE;
  }

  return title;
};

export const processCrashLogs = ({ crashLogs, job }) => crashLogs.crash.entries.map(
  ({ ts, stackTrace, ...rest }) => (
    job.os === 'Android' ? {
      title: extractAndroidTitle(stackTrace),
      description: stackTrace,
      timestamp: WebDriverCommandsUtils.formatDuration(ts * 1000),
      rawTs: ts,
      ...rest,
    } : {
      ...extractiOStitleandDesc(stackTrace),
      timestamp: WebDriverCommandsUtils.formatDuration(ts * 1000),
      rawTs: ts,
      ...rest,
    }));
